/**
 * Determines the folder IDs that are common to all selected stories.
 *
 * This function evaluates the intersection of folder IDs across multiple stories
 * to identify which folders are shared by all the selected stories. The behavior
 * varies based on the number of selected stories:
 *
 * - If no stories are selected, it returns an empty array.
 * - If only one story is selected, it returns the folder IDs associated with that story.
 * - If multiple stories are selected, it returns the folder IDs that are common to all of them.
 *
 * @param selectedStoriesIds - An array of IDs representing the selected stories.
 * @param folderStories - A list of all stories, each containing its associated folders.
 * @param foldersProject - A list of all folders in the project.
 * @returns An array of folder IDs that are common to all selected stories.
 */

import type { Folder, Story } from "@theirstory/common/api_types/backend";

export const getCommonFolders = (selectedStoriesIds: string[], folderStories: Story[], foldersProject: Folder[]) => {
  if (selectedStoriesIds.length === 0) return [];

  if (selectedStoriesIds.length === 1) {
    const story = folderStories.find((storyInFolder) => storyInFolder._id === selectedStoriesIds[0]);
    return story?.folders?.map((folder) => folder._id) || [];
  }

  const commonFoldersIds: string[] = [];

  const allFolderIds = foldersProject.map((folder) => folder._id);

  for (const folderId of allFolderIds) {
    let allStoriesInFolder = true;

    for (const storyId of selectedStoriesIds) {
      const story = folderStories.find((s) => s._id === storyId);
      const storyFolderIds = story?.folders?.map((f) => f._id) || [];

      if (!storyFolderIds.includes(folderId)) {
        allStoriesInFolder = false;
        break;
      }
    }

    if (allStoriesInFolder) {
      commonFoldersIds.push(folderId);
    }
  }

  return commonFoldersIds;
};
